// src/utils/activityLogger.ts - FastMag Activity Logger

import { supabase } from '@/lib/supabase';
import type { ActivityLog } from '@/types';

/**
 * Activity categories for FastMag (must match database ENUM)
 */
export const ACTIVITY_CATEGORIES = {
    ARTICLE: 'article',
    CATEGORY: 'category',
    LOCATION: 'location',
    AUTH: 'auth',
    SYSTEM: 'system'
} as const;

export type ActivityCategory = typeof ACTIVITY_CATEGORIES[keyof typeof ACTIVITY_CATEGORIES];

/**
 * Activity actions for FastMag (must match database ENUM)
 */
export const ACTIVITY_ACTIONS = {
    CREATED: 'created',
    UPDATED: 'updated',
    DELETED: 'deleted',
    QUANTITY_INCREASED: 'quantity_increased',
    QUANTITY_DECREASED: 'quantity_decreased',
    ASSIGNED: 'assigned',
    UNASSIGNED: 'unassigned',
    MOVED: 'moved',
    LOGIN: 'login',
    LOGOUT: 'logout'
} as const;

export type ActivityAction = typeof ACTIVITY_ACTIONS[keyof typeof ACTIVITY_ACTIONS];

/**
 * Parameters for logging activity
 */
export interface LogActivityParams {
    action_category: ActivityCategory;
    action_type: ActivityAction;
    description: string;
    entity_id?: string | null;
    entity_type?: string | null;
    metadata?: Record<string, any>;
}

/**
 * Log user activity to activity_logs table
 */
export async function logActivity({
    action_category,
    action_type,
    entity_id = null,
    entity_type = null,
    description,
    metadata = {}
}: LogActivityParams): Promise<void> {
    try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
            console.warn('No session found for activity logging');
            return;
        }

        // Get customer_uuid from profile
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('customer_uuid')
            .eq('id', session.user.id)
            .single();

        if (profileError || !profile?.customer_uuid) {
            console.error('No customer_uuid found for user:', profileError);
            return;
        }

        const logData: Partial<ActivityLog> = {
            customer_uuid: profile.customer_uuid,
            user_id: session.user.id,
            action_category,
            action_type,
            entity_id: entity_id ? String(entity_id) : null,
            entity_type,
            description,
            metadata: metadata || {}
        };

        // Insert activity log
        const { error } = await supabase
            .from('activity_logs')
            .insert(logData);

        if (error) {
            console.error('Activity log error:', error);
        }

    } catch (error) {
        console.error('Activity log failed:', error);
        // Don't throw - logging failures shouldn't break the main flow
    }
}

/**
 * Helper functions for common activities
 */

// Article activities
export const logArticleCreated = (articleId: string, articleCode: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.ARTICLE,
        action_type: ACTIVITY_ACTIONS.CREATED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Articolo creato: ${articleCode}`,
        metadata: { articleCode }
    });

export const logArticleUpdated = (articleId: string, articleCode: string, changes: Record<string, any> = {}): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.ARTICLE,
        action_type: ACTIVITY_ACTIONS.UPDATED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Articolo aggiornato: ${articleCode}`,
        metadata: { articleCode, changes }
    });

export const logArticleDeleted = (articleId: string, articleCode: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.ARTICLE,
        action_type: ACTIVITY_ACTIONS.DELETED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Articolo eliminato: ${articleCode}`,
        metadata: { articleCode }
    });

export const logCompatibilityAdded = (articleId: string, articleCode: string, compatibleCode: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.ARTICLE,
        action_type: ACTIVITY_ACTIONS.ASSIGNED,
        entity_id: articleId,
        entity_type: 'article_compatibility',
        description: `Compatibilità aggiunta: ${articleCode} ↔ ${compatibleCode}`,
        metadata: { articleCode, compatibleCode }
    });

export const logCompatibilityRemoved = (articleId: string, articleCode: string, compatibleCode: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.ARTICLE,
        action_type: ACTIVITY_ACTIONS.UNASSIGNED,
        entity_id: articleId,
        entity_type: 'article_compatibility',
        description: `Compatibilità rimossa: ${articleCode} ↔ ${compatibleCode}`,
        metadata: { articleCode, compatibleCode }
    });

export const logQuantityAdjusted = (articleId: string, articleCode: string, delta: number, newQuantity: number): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.ARTICLE,
        action_type: delta > 0 ? ACTIVITY_ACTIONS.QUANTITY_INCREASED : ACTIVITY_ACTIONS.QUANTITY_DECREASED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Quantità ${delta > 0 ? 'aumentata' : 'diminuita'} di ${Math.abs(delta)} per ${articleCode} (Totale: ${newQuantity})`,
        metadata: { articleCode, delta, newQuantity, previousQuantity: newQuantity - delta }
    });

// Location activities
export const logLocationCreated = (locationId: string, locationName: string, parentPath: string | null = null): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.LOCATION,
        action_type: ACTIVITY_ACTIONS.CREATED,
        entity_id: locationId,
        entity_type: 'location',
        description: parentPath ? `Posizione creata: ${locationName} in ${parentPath}` : `Posizione creata: ${locationName}`,
        metadata: { locationName, parentPath }
    });

export const logLocationUpdated = (locationId: string, locationName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.LOCATION,
        action_type: ACTIVITY_ACTIONS.UPDATED,
        entity_id: locationId,
        entity_type: 'location',
        description: `Posizione aggiornata: ${locationName}`,
        metadata: { locationName }
    });

export const logLocationDeleted = (locationId: string, locationName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.LOCATION,
        action_type: ACTIVITY_ACTIONS.DELETED,
        entity_id: locationId,
        entity_type: 'location',
        description: `Posizione eliminata: ${locationName}`,
        metadata: { locationName }
    });

export const logArticleAssigned = (articleId: string, articleCode: string, locationPath: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.LOCATION,
        action_type: ACTIVITY_ACTIONS.ASSIGNED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Articolo ${articleCode} assegnato a ${locationPath}`,
        metadata: { articleCode, locationPath }
    });

export const logArticleUnassigned = (articleId: string, articleCode: string, locationPath: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.LOCATION,
        action_type: ACTIVITY_ACTIONS.UNASSIGNED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Articolo ${articleCode} rimosso da ${locationPath}`,
        metadata: { articleCode, locationPath }
    });

export const logArticleMoved = (articleId: string, articleCode: string, fromLocation: string, toLocation: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.LOCATION,
        action_type: ACTIVITY_ACTIONS.MOVED,
        entity_id: articleId,
        entity_type: 'article',
        description: `Articolo ${articleCode} spostato da ${fromLocation} a ${toLocation}`,
        metadata: { articleCode, fromLocation, toLocation }
    });

// Category activities
export const logCategoryCreated = (categoryId: string, categoryName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.CATEGORY,
        action_type: ACTIVITY_ACTIONS.CREATED,
        entity_id: categoryId,
        entity_type: 'category',
        description: `Categoria creata: ${categoryName}`,
        metadata: { categoryName }
    });

export const logCategoryUpdated = (categoryId: string, categoryName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.CATEGORY,
        action_type: ACTIVITY_ACTIONS.UPDATED,
        entity_id: categoryId,
        entity_type: 'category',
        description: `Categoria aggiornata: ${categoryName}`,
        metadata: { categoryName }
    });

export const logCategoryDeleted = (categoryId: string, categoryName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.CATEGORY,
        action_type: ACTIVITY_ACTIONS.DELETED,
        entity_id: categoryId,
        entity_type: 'category',
        description: `Categoria eliminata: ${categoryName}`,
        metadata: { categoryName }
    });

export const logSubcategoryCreated = (subcategoryId: string, subcategoryName: string, categoryName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.CATEGORY,
        action_type: ACTIVITY_ACTIONS.CREATED,
        entity_id: subcategoryId,
        entity_type: 'subcategory',
        description: `Sottocategoria creata: ${subcategoryName} in ${categoryName}`,
        metadata: { subcategoryName, categoryName }
    });

export const logSubcategoryUpdated = (subcategoryId: string, subcategoryName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.CATEGORY,
        action_type: ACTIVITY_ACTIONS.UPDATED,
        entity_id: subcategoryId,
        entity_type: 'subcategory',
        description: `Sottocategoria aggiornata: ${subcategoryName}`,
        metadata: { subcategoryName }
    });

export const logSubcategoryDeleted = (subcategoryId: string, subcategoryName: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.CATEGORY,
        action_type: ACTIVITY_ACTIONS.DELETED,
        entity_id: subcategoryId,
        entity_type: 'subcategory',
        description: `Sottocategoria eliminata: ${subcategoryName}`,
        metadata: { subcategoryName }
    });

// Auth activities
export const logUserLogin = (email: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.AUTH,
        action_type: ACTIVITY_ACTIONS.LOGIN,
        entity_type: 'user',
        description: `Utente effettuato login: ${email}`,
        metadata: { email }
    });

export const logUserLogout = (email: string): Promise<void> =>
    logActivity({
        action_category: ACTIVITY_CATEGORIES.AUTH,
        action_type: ACTIVITY_ACTIONS.LOGOUT,
        entity_type: 'user',
        description: `Utente effettuato logout: ${email}`,
        metadata: { email }
    });
